import * as React from 'react';
import {
  FiSave,
  FiUpload,
} from 'react-icons/fi';
import { IoMdTrash } from 'react-icons/io';
import { action } from 'mobx';
import { inject } from 'mobx-react';
import { BsDownload } from 'react-icons/bs';
import { Button, ButtonProps } from '../Button/Button';
import {
  FlexLayout,
  LayoutTitle,
  LoaderButton,
  IFrame,
  CheckBox,
  TextBox,
  ImageView,
  DragAndDropLayout,
} from '../..';
import { Global } from '../../../constants';
import { Format, Sha256 } from '../../../utils/string';
import { ConfirmFail, ConfirmSuccess, ConfirmWarning } from '../../../utils/confirm';
import { FileSelector } from '../../../utils/file';
import { ActionStore, PublicStore } from '../../../stores';
import { AttachmentInfo } from '../../../models';
import { ToBase64 } from '../../../utils/binary';
import Resizer from '../../../utils/image/Resizer';
import { registerAttachTarget } from '../../../../agent/attachTarget';
import { findDOMNode } from 'react-dom';

const fileDownload = require('js-file-download');

interface CustomAttachmentBoxState {
  attachments: Array<AttachmentInfo>;
  percent: number;
  iframe?: string;
  preview: number;
}

interface CustomAttachmentBoxProps extends ButtonProps {
  publicStore?: PublicStore;
  actionStore?: ActionStore;
  window: string;
  params: {};
  isEnabledTitle?: boolean; // 제목 기입 가능 여부
  isEnabledPreview?: boolean; // 미리보기 여부
  onChangeCount?: (count: number) => any;
  onPreview?: (type: PREVIEW_OPTION, uri: string) => any;
}

export enum PREVIEW_OPTION {
  IMAGE,
  PDF,
  NONE,
}

@inject('publicStore', 'actionStore')
export class CustomAttachmentBox extends React.Component<
  CustomAttachmentBoxProps,
  CustomAttachmentBoxState
> {
  table: React.RefObject<DragAndDropLayout>;

  constructor(props: CustomAttachmentBoxProps, context: any) {
    super(props, context);
    this.table = React.createRef();
    this.state = {
      attachments: [],
      percent: 0,
      preview: PREVIEW_OPTION.NONE,
    };
  }

  /** 에이전트에게 「여기에 붙일 수 있다」고 알린 것을 거두는 함수. */
  private unregisterAgent?: () => void;

  componentDidMount() {
    this.retrieve();
    // **상자가 스스로 등록한다.** 이 상자는 문서를 `window` 와 `params` 로 가리킨다 —
    // 밖에서 알아낼 방법이 없다. 업로드도 이 상자가 하던 그대로 쓴다.
    const box = this;
    this.unregisterAgent = registerAttachTarget({
      // **값이 하나라도 차 있어야 문서가 정해진 것이다.** 키는 언제나 있고 값만
      // 비어 있다(`recedate: model?.recedate`) — 키 수만 세면 늘 참이 된다.
      ready: () => Object.values(box.props.params ?? {})
        .some((one) => String(one ?? '').trim()),
      node: () => findDOMNode(box) as Element | null,
      accept: async (files: File[]) => {
        for (let i = 0; i < files.length; i += 1) {
          // eslint-disable-next-line no-await-in-loop
          await this.uploadAttachment(files[i].name, files[i], i);
        }
      },
    });
  }

  componentDidUpdate(prevProps: Readonly<CustomAttachmentBoxProps>) {
    if (Sha256.make(JSON.stringify(prevProps.params)) !== Sha256.make(JSON.stringify(this.props.params))) {
      this.retrieve();
    }
  }

  componentWillUnmount() {
    this.unregisterAgent?.();
    if (this.state.attachments.length > 0) {
      this.save();
    }
  }

  @action
  async retrieve() {
    const { actionStore: api, publicStore } = this.props;

    const data = await api!.attachment(
      {
        sub: this.props.window,
        custcd: publicStore?.user.custcd,
        spjangcd: publicStore?.user.spjangcd,
        ...this.props.params,
      },
      'retrieve',
    );

    if (data) {
      this.setState({
        attachments: data.items?.map((x: any) => new AttachmentInfo({ ...x, sub: this.props.window })) || [],
      }, async () => {
        await this.table.current?.update();
        if (data.items?.length) this.table.current?.setFocus(0, 3);
      });
    }
  }

  @action
  async addAttachment() {
    try {
      const files = await FileSelector.multi(false);
      for (let i = 0; i < files.length; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await this.uploadAttachment(files[i].name, files[i], i);
      }
    } catch {
      ConfirmFail.show('오류', '파일 처리중 알 수 없는 문제가 발생하였습니다.');
    }
  }

  @action
  async uploadAttachment(filename: string, willUpload: File, index: number) {
    const { actionStore: api, publicStore } = this.props;

    const file = await Resizer.rotateImageIfNeeded(willUpload);

    // **끝을 기다릴 수 있어야 한다.** 예전에는 이 아래가 통째로 setState 콜백 안에
    // 있어서 부르는 쪽이 언제 끝났는지 알 수 없었다 — 에이전트가 여러 장을 붙일 때
    // 순번(`seq`)이 겹친다. 하는 일과 순서는 그대로 두고 기다릴 수 있게만 폈다.
    await new Promise<void>((done) => this.setState({ percent: 1 }, done));

    const { data } = await api?.tempUpload(file, filename, (e: any) => {
      const percent = Math.round((e.loaded / e.total) * 100.0) || 1;
      this.updatePercent(percent);
    });

    if (!data) {
      this.updatePercent(0);
      return;
    }

    const attachment = await api?.attachment(
      {
        sub: this.props.window,
        custcd: publicStore?.user.custcd,
        spjangcd: publicStore?.user.spjangcd,
        ...this.props.params,
        seq: `000${this.getLastFileSeq() + 1 + index}`.substr(-3),
        title: '',
        tempfile: data,
      },
      'upload',
    );

    if (!attachment) {
      this.updatePercent(0);
      return;
    }

    await new Promise<void>((done) => this.setState({
      attachments: [...this.state.attachments, new AttachmentInfo(attachment)],
    }, done));
    this.updatePercent(0);
    this.table.current?.update(false);
  }

  getLastFileSeq() {
    if (!this.state.attachments.length) return 0;
    const { seq } = this.state.attachments[this.state.attachments.length - 1];
    return parseInt(seq, 10);
  }

  @action
  async save() {
    const { actionStore: api } = this.props;
    const data = await api?.attachment(
      {
        sub: this.props.window,
        items: this.state.attachments.map((x, i) => ({
          new: '0',
          spjangcd: x.spjangcd,
          ...this.props.params,
          seq: x.seq,
          sort: `000${i}`.substr(-3),
          fileseq: x.fileseq,
          title: x.title,
        })),
      },
      'save',
    );
    ConfirmSuccess.showPbMessage(data);
    this.props.onChangeCount && this.props.onChangeCount(this.state.attachments.length);
  }

  @action
  updatePercent(percent: number) {
    this.setState({ percent });
  }

  @action
  async attachmentBinary(info: AttachmentInfo): Promise<ArrayBuffer> {
    const { actionStore: api } = this.props;
    return api!.attachmentBinary({
      ...info,
      sub: this.props.window,
      ...this.props.params,
      seq: info.seq,
    });
  }

  @action
  async attachmentDownload(info: AttachmentInfo) {
    const binary = await this.attachmentBinary(info);

    try {
      fileDownload(binary, `${info.filename}.${info.extension}`.trim());
    } catch {
      ConfirmFail.show('오류', '다운로드에 실패하였습니다.');
    }
  }

  @action
  async attachmentRemove(): Promise<boolean> {
    const { actionStore: api } = this.props;
    const items = this.state.attachments.filter((x) => x.delete);

    if (!items.length) {
      ConfirmWarning.show('오류', '삭제할 파일이 없습니다!');
      return false;
    }

    try {
      const data = await api?.attachment(
        {
          sub: this.props.window,
          items: this.state.attachments.filter((x) => x.delete).map((x) => ({
            ...this.props.params,
            seq: x.seq,
          })),
        },
        'wb_delete',
      );

      if (data) {
        ConfirmSuccess.showPbMessage(data);

        this.setState({
          attachments: this.state.attachments
            .filter((x) => !x.delete),
        }, () => this.table.current?.update(false));
      } else {
        ConfirmFail.show('오류', '서버 오류로 첨부파일이 삭제되지 않았습니다!');
      }

      return true;
    } catch {
      return false;
    }
  }

  render() {
    const {
      percent,
    } = this.state;

    return (
      <FlexLayout isVertical={true}>
        <FlexLayout size={Global.LAYOUT_GRID_HEIGHT_1}>
          <LayoutTitle weight={1}>
            첨부 파일
          </LayoutTitle>
          <Button
            onClick={() => this.attachmentRemove()}
          >
            <IoMdTrash />
            <span>삭제</span>
          </Button>
          {this.props.isEnabledTitle && <Button
            onClick={() => this.save()}
          >
            <FiSave />
            <span>저장</span>
          </Button>}
          <LoaderButton
            onClick={() => this.addAttachment()}
            percent={percent}
          >
            <FiUpload />
            <span>내 PC</span>
          </LoaderButton>
        </FlexLayout>

        <FlexLayout>
          <FlexLayout weight={2}>
            <DragAndDropLayout
              ref={this.table}
              header={[
                {
                  id: 'seq',
                  text: '순번',
                  width: 10,
                }, {
                  id: 'delete',
                  text: '삭제',
                  width: 10,
                  render: (x: any, rowUpdate) => (
                    <FlexLayout justify="center" align="center">
                      <CheckBox
                        noMargin
                        value={x.delete}
                        onChange={(v) => rowUpdate({ delete: v })}
                      />
                    </FlexLayout>
                  ),
                }, {
                  id: 'download',
                  text: '다운로드',
                  width: 10,
                  render: (x: any) => (
                    <FlexLayout justify="center" align="center">
                      <Button
                        isIcon={true}
                        onClick={() => this.attachmentDownload(x)}
                      >
                        <BsDownload size={19} />
                      </Button>
                    </FlexLayout>
                  ),
                }, {
                  id: 'filename',
                  text: '파일명',
                  width: this.props.isEnabledTitle ? 20 : 40,
                  render: (x: any) => (
                    <FlexLayout justify="start" align="center">
                      {x.filename}
                    </FlexLayout>
                  ),
                }, {
                  id: 'title',
                  text: '제목',
                  width: 40,
                  isHidden: !this.props.isEnabledTitle,
                  render: (x: any, rowUpdate, refs) => (
                    <TextBox
                      ref={refs}
                      value={x.title}
                      onChange={(v) => rowUpdate({ title: v })}
                    />
                  ),
                }, {
                  id: 'extension',
                  text: '확장자',
                  width: 10,
                }, {
                  id: 'size',
                  text: '파일크기',
                  width: 10,
                  render: (x: any) => (
                    <FlexLayout justify={x.size ? 'end' : 'center'} align="center">
                      {x.size ? `${Format.number(x.size)}B` : '모름'}
                    </FlexLayout>
                  ),
                },
              ]}
              rowHeight={Global.LAYOUT_GRID_HEIGHT_1}
              data={this.state.attachments || []}
              onChange={(rows: Array<AttachmentInfo>) => {
                this.setState({ attachments: rows });
              }}
              onRowFocusEvent={async (item) => {
                const binary = await this.attachmentBinary(item);
                let option = PREVIEW_OPTION.NONE;
                let mimeType = '';

                switch (item.extension.toLowerCase()) {
                  case 'jpg':
                  case 'jpeg':
                  case 'gif':
                  case 'bmp':
                  case 'png':
                    option = PREVIEW_OPTION.IMAGE;
                    mimeType = `image/${item.extension.toLowerCase()}`;
                    break;

                  case 'pdf':
                    option = PREVIEW_OPTION.PDF;
                    mimeType = 'application/pdf';
                    break;

                  default:
                  //
                }

                const b64 = await ToBase64.fromArrayBuffer(binary);

                this.setState({
                  iframe: `data:${mimeType};base64,${b64}`,
                  preview: option,
                });

                this.props.onPreview && this.props.onPreview(
                  option,
                  `data:${mimeType};base64,${b64}`,
                );
              }}
            />
          </FlexLayout>
          {this.props.isEnabledPreview && <FlexLayout weight={1} isVertical={true}>
            <LayoutTitle>
              미리보기
            </LayoutTitle>
            {this.state.preview === PREVIEW_OPTION.IMAGE
              && <ImageView src={this.state.iframe} style={{ marginBottom: 0 }} />}
            {this.state.preview === PREVIEW_OPTION.PDF
              && <IFrame src={this.state.iframe} />}
            {this.state.preview === PREVIEW_OPTION.NONE
              && <IFrame src={this.state.iframe} />}
          </FlexLayout>}
        </FlexLayout>
      </FlexLayout>
    );
  }
}
